import { z } from 'zod'

/**
 * Zod-helpers voor query-string-parameters (FO-488, batch-66).
 *
 * Een query-string kent alleen tekst. `z.coerce.boolean()` doet `Boolean(waarde)`
 * en elke niet-lege string is truthy: `?superseded=false` en `?actief=0` werden
 * daardoor `true` en het filter klapte stil om. Zo vond de Trends-modal na "Nu
 * berekenen" de verse run niet terug (de lijst zocht naar vervangen runs) en
 * gaf het filter "Alleen vers" op Reisafstanden juist de verouderde rijen.
 *
 * Gebruik daarom voor elke boolean in een `request.query`-schema `queryBoolean()`.
 * De drift-guard `apps/api/src/lib/__tests__/query-boolean-drift.test.ts` faalt
 * op een nieuwe `z.coerce.boolean`.
 */

/** Melding bij een waarde die geen boolean voorstelt (landt inline op het veld). */
export const QUERY_BOOLEAN_MELDING = 'Ongeldige waarde: gebruik true of false (1 of 0 mag ook)'

/**
 * Boolean uit een query-string: `'true'`/`'1'` wordt `true`, `'false'`/`'0'`
 * wordt `false`. Al het andere (`'ja'`, `'True'`, een lege waarde, een herhaalde
 * parameter) is een validatiefout op het veld, geen stille gok.
 *
 * Een echte boolean wordt ook geaccepteerd. Hetzelfde filterschema wordt soms
 * met JSON gevoed (een export-body die de lijstfilters spiegelt, een server-
 * pagina die zijn eigen parameters valideert) en daar is `false` gewoon `false`.
 *
 * Het veld is verplicht; ketting zelf door voor de andere vormen:
 *
 *   queryBoolean().optional()      // ontbreekt -> undefined (filter niet gezet)
 *   queryBoolean().default(true)   // ontbreekt -> true
 *
 * Let op de volgorde bij een eigen `.transform()`: zet `.optional()` altijd NA
 * `queryBoolean()`. Het oudere patroon `.optional().transform((v) => v === 'true')`
 * maakt van een ontbrekende parameter `false`, en dan is "niet gezet" niet meer
 * te onderscheiden van "uit".
 */
export function queryBoolean() {
  return z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])], { error: QUERY_BOOLEAN_MELDING })
    .transform((waarde) => waarde === true || waarde === 'true' || waarde === '1')
}
